import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Chip, Paper, CircularProgress } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import StraightenIcon from '@mui/icons-material/Straighten';
import PlaceIcon from '@mui/icons-material/Place';
import { locationService } from '../../services/locationService';
import { smartWarehouseService } from '../../services/smartWarehouseService';
import {
  aStarPath,
  getAllAisleWalkableCells,
  entryCenterX,
  entryCenterY,
} from '../../components/map/mapTypes';
import type {
  LocationMapItem,
  EntryExitItem,
  ObstacleItem,
  RoutePoint,
} from '../../components/map/mapTypes';

interface ReceivingNavMapProps {
  warehouseId: string;
  /** Vurgulanacak hedef lokasyon (önerilen/aktif raf). */
  targetLocationId?: string | null;
  /** Hedef için gösterilecek okunabilir kod (öneri kodu). */
  targetLocationCode?: string | null;
  /** Yerleştirilmiş (tamamlanmış) lokasyonlar — yeşil. */
  placedLocationIds?: Set<string>;
  /** Bekleyen diğer öneri lokasyonları — gri vurgusu. */
  pendingLocationIds?: Set<string>;
  height?: number | string;
  /** Üst şeritte gösterilecek ek konum özeti. */
  subtitle?: string;
}

interface MapData {
  locations: LocationMapItem[];
  entryExits: EntryExitItem[];
  obstacles: ObstacleItem[];
  warehouseWidth: number;
  warehouseHeight: number;
}

const COLOR_TARGET = '#fb8c00';
const COLOR_PLACED = '#43a047';
const COLOR_PENDING = '#90a4ae';
const COLOR_OTHER = '#cfd8dc';
const COLOR_AISLE = '#fafafa';
const COLOR_PLATFORM = '#e0e0e0';
const COLOR_ZONE_BG = 'rgba(33, 150, 243, 0.05)';
const COLOR_OBSTACLE = '#616161';
const COLOR_ROUTE = '#1565c0';
const BG = '#f7fafc';

function flattenTreeToLocations(nodes: any[], parentId: string | null = null): LocationMapItem[] {
  const out: LocationMapItem[] = [];
  for (const n of nodes || []) {
    const id = n.Id || n.id;
    const type = (n.Type || n.type) as LocationMapItem['type'];
    if (type !== 'Zone' && type !== 'Aisle' && type !== 'Platform' && type !== 'Rack' && type !== 'Level' && type !== 'Bin') {
      const children = n.Children || n.children || [];
      if (children.length) out.push(...flattenTreeToLocations(children, id ?? parentId));
      continue;
    }
    out.push({
      id,
      type,
      code: n.Code || n.code || '',
      name: n.Name || n.name || '',
      gridX: n.GridX ?? n.gridX ?? 0,
      gridY: n.GridY ?? n.gridY ?? 0,
      gridWidth: n.GridWidth ?? n.gridWidth ?? (type === 'Rack' ? 1 : type === 'Zone' ? 8 : type === 'Aisle' ? 2 : 3),
      gridHeight: n.GridHeight ?? n.gridHeight ?? (type === 'Rack' ? 1 : type === 'Zone' ? 6 : 10),
      parentId: n.ParentId ?? n.parentId ?? parentId,
      aisleId: n.AisleId ?? n.aisleId ?? null,
      aisleSide: n.AisleSide ?? n.aisleSide ?? null,
      sortOrder: n.SortOrder ?? n.sortOrder ?? 0,
      isPickable: n.IsPickable ?? n.isPickable ?? (type === 'Rack'),
      isNew: false,
      isModified: false,
      isDeleted: false,
    });
    const children = n.Children || n.children || [];
    if (children.length) out.push(...flattenTreeToLocations(children, id ?? parentId));
  }
  return out;
}

function mapFlatLocations(rows: any[]): LocationMapItem[] {
  return (rows || [])
    .map((n: any) => {
      const type = (n.Type || n.type) as LocationMapItem['type'];
      if (type !== 'Zone' && type !== 'Aisle' && type !== 'Platform' && type !== 'Rack' && type !== 'Level' && type !== 'Bin') {
        return null;
      }
      return {
        id: n.Id || n.id,
        type,
        code: n.Code || n.code || '',
        name: n.Name || n.name || '',
        gridX: n.GridX ?? n.gridX ?? 0,
        gridY: n.GridY ?? n.gridY ?? 0,
        gridWidth: n.GridWidth ?? n.gridWidth ?? (type === 'Rack' ? 1 : type === 'Zone' ? 8 : type === 'Aisle' ? 2 : 3),
        gridHeight: n.GridHeight ?? n.gridHeight ?? (type === 'Rack' ? 1 : type === 'Zone' ? 6 : 10),
        parentId: n.ParentId ?? n.parentId ?? null,
        aisleId: n.AisleId ?? n.aisleId ?? null,
        aisleSide: n.AisleSide ?? n.aisleSide ?? null,
        sortOrder: n.SortOrder ?? n.sortOrder ?? 0,
        isPickable: n.IsPickable ?? n.isPickable ?? (type === 'Rack'),
        isNew: false,
        isModified: false,
        isDeleted: false,
      } as LocationMapItem;
    })
    .filter((x: any): x is LocationMapItem => !!x);
}

function mapEntryExits(nodes: any[]): EntryExitItem[] {
  return (nodes || [])
    .filter((n: any) => {
      const nt = n.NodeType || n.nodeType || '';
      return nt === 'Entry' || nt === 'Exit';
    })
    .map((n: any) => ({
      id: n.Id || n.id,
      nodeType: (n.NodeType || n.nodeType) as 'Entry' | 'Exit',
      name: n.Name || n.name || '',
      gridX: n.GridX ?? n.gridX ?? 0,
      gridY: n.GridY ?? n.gridY ?? 0,
      isNew: false,
      isModified: false,
      isDeleted: false,
    }));
}

function mapObstacles(rows: any[]): ObstacleItem[] {
  return (rows || []).map((n: any) => ({
    id: n.Id || n.id,
    obstacleType: (n.ObstacleType || n.obstacleType || 'Other') as ObstacleItem['obstacleType'],
    gridX: n.GridX ?? n.gridX ?? 0,
    gridY: n.GridY ?? n.gridY ?? 0,
    gridWidth: n.GridWidth ?? n.gridWidth ?? 1,
    gridHeight: n.GridHeight ?? n.gridHeight ?? 1,
    isNew: false,
    isModified: false,
    isDeleted: false,
  }));
}

const ReceivingNavMap: React.FC<ReceivingNavMapProps> = ({
  warehouseId,
  targetLocationId,
  targetLocationCode,
  placedLocationIds,
  pendingLocationIds,
  height = 260,
  subtitle,
}) => {
  const { t } = useTranslation('warehouse');
  const [data, setData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!warehouseId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);

        let locations: LocationMapItem[] = [];
        try {
          const treeRes: any = await locationService.getTree(warehouseId);
          const treeData = treeRes?.data?.data || treeRes?.data?.Data || treeRes?.data || [];
          locations = flattenTreeToLocations(treeData);
        } catch {
          const flatRes: any = await locationService.getByWarehouse(warehouseId);
          const flatData = flatRes?.data?.data || flatRes?.data?.Data || flatRes?.data || [];
          locations = mapFlatLocations(flatData);
        }

        let entryExits: EntryExitItem[] = [];
        try {
          const eeRes: any = await smartWarehouseService.getEntryExits(warehouseId);
          const eeData = eeRes?.data?.data || eeRes?.data?.Data || eeRes?.data || [];
          entryExits = mapEntryExits(eeData);
        } catch {
          entryExits = [];
        }

        let obstacles: ObstacleItem[] = [];
        try {
          const obsRes: any = await locationService.getObstacles(warehouseId);
          const obsData = obsRes?.data?.data || obsRes?.data?.Data || obsRes?.data || [];
          obstacles = mapObstacles(obsData);
        } catch {
          obstacles = [];
        }

        let maxX = 0, maxY = 0;
        for (const l of locations) {
          maxX = Math.max(maxX, l.gridX + (l.gridWidth || 1));
          maxY = Math.max(maxY, l.gridY + (l.gridHeight || 1));
        }
        for (const ee of entryExits) {
          maxX = Math.max(maxX, ee.gridX + 2);
          maxY = Math.max(maxY, ee.gridY + 2);
        }
        for (const ob of obstacles) {
          maxX = Math.max(maxX, ob.gridX + (ob.gridWidth || 1));
          maxY = Math.max(maxY, ob.gridY + (ob.gridHeight || 1));
        }
        const warehouseWidth = Math.max(20, maxX);
        const warehouseHeight = Math.max(15, maxY);

        if (cancelled) return;
        setData({ locations, entryExits, obstacles, warehouseWidth, warehouseHeight });
      } catch (err: any) {
        if (!cancelled) setError(err?.message || t('screens.receiving.navMap.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [warehouseId, t]);

  const cellPx = useMemo(() => {
    if (!data || containerSize.w <= 0 || containerSize.h <= 0) return 14;
    const padding = 8;
    const sx = (containerSize.w - padding * 2) / data.warehouseWidth;
    const sy = (containerSize.h - padding * 2) / data.warehouseHeight;
    return Math.max(6, Math.min(sx, sy));
  }, [data, containerSize]);

  const targetLoc = useMemo(() => {
    if (!data || !targetLocationId) return null;
    return data.locations.find(l => l.id === targetLocationId) || null;
  }, [data, targetLocationId]);

  const route = useMemo<RoutePoint[]>(() => {
    if (!data || !targetLoc) return [];
    const walkable = getAllAisleWalkableCells(data.locations);

    const entry = data.entryExits.find(e => e.nodeType === 'Entry') || data.entryExits[0];
    let startPoint: RoutePoint;
    if (entry) {
      startPoint = { gridX: Math.floor(entryCenterX(entry)), gridY: Math.floor(entryCenterY(entry)) };
    } else {
      startPoint = { gridX: Math.max(0, targetLoc.gridX - 1), gridY: targetLoc.gridY };
    }

    const endPoint: RoutePoint = { gridX: targetLoc.gridX, gridY: targetLoc.gridY };

    const snapToNearestWalkable = (p: RoutePoint): RoutePoint => {
      const k = (x: number, y: number) => `${x},${y}`;
      if (walkable.has(k(Math.round(p.gridX), Math.round(p.gridY)))) return p;
      let best: RoutePoint | null = null;
      let bestD = Number.POSITIVE_INFINITY;
      walkable.forEach(s => {
        const [sxStr, syStr] = s.split(',');
        const sx = Number(sxStr), sy = Number(syStr);
        const d = Math.abs(sx - p.gridX) + Math.abs(sy - p.gridY);
        if (d < bestD) { bestD = d; best = { gridX: sx, gridY: sy }; }
      });
      return best || p;
    };

    const aStart = snapToNearestWalkable(startPoint);
    const aEnd = snapToNearestWalkable(endPoint);
    const path = aStarPath(aStart, aEnd, data.obstacles, {
      width: data.warehouseWidth,
      height: data.warehouseHeight,
    }, walkable);

    if (path.length > 0) {
      const last = path[path.length - 1];
      if (last.gridX !== endPoint.gridX || last.gridY !== endPoint.gridY) {
        path.push(endPoint);
      }
    }
    return path;
  }, [data, targetLoc]);

  const distance = useMemo(() => {
    if (route.length < 2) return 0;
    let d = 0;
    for (let i = 1; i < route.length; i++) {
      d += Math.abs(route[i].gridX - route[i - 1].gridX) + Math.abs(route[i].gridY - route[i - 1].gridY);
    }
    return d;
  }, [route]);

  if (!warehouseId) return null;

  const targetCode = targetLoc?.code || targetLocationCode || t('screens.receiving.navMap.noTarget');

  return (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Chip
          icon={<PlaceIcon sx={{ color: '#fff !important', fontSize: '16px !important' }} />}
          label={targetCode}
          size="small"
          sx={{
            bgcolor: COLOR_TARGET,
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.8rem',
            height: 28,
            letterSpacing: 0.2,
            '& .MuiChip-icon': { color: '#fff', ml: 0.5 },
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
        <Chip
          icon={<StraightenIcon sx={{ fontSize: '14px !important' }} />}
          label={distance > 0 ? t('screens.receiving.navMap.steps', { count: distance }) : '—'}
          size="small"
          variant="outlined"
          sx={{ height: 26, fontSize: '0.7rem' }}
        />
        {subtitle ? (
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: 12,
              lineHeight: 1.2,
              wordBreak: 'break-word',
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
          bgcolor: BG,
          overflow: 'hidden',
          flexShrink: 0,
        }}
        ref={wrapperRef}
      >
        {loading && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="caption" color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && data && (
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: data.warehouseWidth * cellPx,
              height: data.warehouseHeight * cellPx,
            }}
          >
            {data.locations
              .filter(l => l.type === 'Zone')
              .map(z => (
                <Box key={z.id} sx={{
                  position: 'absolute',
                  left: z.gridX * cellPx, top: z.gridY * cellPx,
                  width: z.gridWidth * cellPx, height: z.gridHeight * cellPx,
                  bgcolor: COLOR_ZONE_BG,
                  border: '1px dashed rgba(33, 150, 243, 0.35)',
                }} />
              ))}

            {data.locations
              .filter(l => l.type === 'Aisle')
              .map(a => (
                <Box key={a.id} sx={{
                  position: 'absolute',
                  left: a.gridX * cellPx, top: a.gridY * cellPx,
                  width: a.gridWidth * cellPx, height: a.gridHeight * cellPx,
                  bgcolor: COLOR_AISLE,
                  borderRadius: 0.5,
                }} />
              ))}

            {data.locations
              .filter(l => l.type === 'Platform')
              .map(p => (
                <Box key={p.id} sx={{
                  position: 'absolute',
                  left: p.gridX * cellPx, top: p.gridY * cellPx,
                  width: p.gridWidth * cellPx, height: p.gridHeight * cellPx,
                  bgcolor: COLOR_PLATFORM,
                  opacity: 0.7,
                }} />
              ))}

            {data.obstacles.map(ob => (
              <Box key={ob.id} sx={{
                position: 'absolute',
                left: ob.gridX * cellPx, top: ob.gridY * cellPx,
                width: ob.gridWidth * cellPx, height: ob.gridHeight * cellPx,
                bgcolor: COLOR_OBSTACLE,
                opacity: 0.55,
                borderRadius: 0.5,
              }} />
            ))}

            {data.locations
              .filter(l => l.type === 'Rack' || l.type === 'Level' || l.type === 'Bin')
              .map(r => {
                const isTarget = targetLocationId === r.id;
                const isPlaced = placedLocationIds?.has(r.id);
                const isPending = pendingLocationIds?.has(r.id);
                const fill = isTarget ? COLOR_TARGET
                  : isPlaced ? COLOR_PLACED
                  : isPending ? COLOR_PENDING
                  : COLOR_OTHER;
                return (
                  <Box key={r.id} sx={{
                    position: 'absolute',
                    left: r.gridX * cellPx, top: r.gridY * cellPx,
                    width: (r.gridWidth || 1) * cellPx,
                    height: (r.gridHeight || 1) * cellPx,
                    bgcolor: fill,
                    borderRadius: 0.5,
                    boxShadow: isTarget ? '0 0 0 2px rgba(251,140,0,0.35)' : 'none',
                    animation: isTarget ? 'receivingRackPulse 1.6s ease-in-out infinite' : 'none',
                  }} />
                );
              })}

            {data.entryExits.map(ee => {
              const isEntry = ee.nodeType === 'Entry';
              const color = isEntry ? '#388e3c' : '#ef6c00';
              return (
                <Box key={ee.id} sx={{
                  position: 'absolute',
                  left: ee.gridX * cellPx,
                  top: ee.gridY * cellPx,
                  width: 2 * cellPx,
                  height: 2 * cellPx,
                  borderRadius: '50%',
                  bgcolor: `${color}22`,
                  border: `1.5px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  {isEntry
                    ? <LoginIcon sx={{ color, fontSize: cellPx * 1.1 }} />
                    : <LogoutIcon sx={{ color, fontSize: cellPx * 1.1 }} />}
                </Box>
              );
            })}

            {route.length > 1 && (
              <svg
                style={{
                  position: 'absolute', left: 0, top: 0,
                  width: '100%', height: '100%',
                  pointerEvents: 'none', overflow: 'visible',
                }}
              >
                <defs>
                  <marker id="receiving-arrow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                    <path d="M0,0 L0,6 L6,3 z" fill={COLOR_ROUTE} />
                  </marker>
                </defs>
                <polyline
                  points={route.map(p => `${(p.gridX + 0.5) * cellPx},${(p.gridY + 0.5) * cellPx}`).join(' ')}
                  fill="none"
                  stroke={COLOR_ROUTE}
                  strokeWidth={Math.max(2, cellPx * 0.18)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={`${cellPx * 0.6} ${cellPx * 0.4}`}
                  markerEnd="url(#receiving-arrow)"
                  style={{ animation: 'receivingRouteDash 1.2s linear infinite' }}
                />
                <circle
                  cx={(route[0].gridX + 0.5) * cellPx}
                  cy={(route[0].gridY + 0.5) * cellPx}
                  r={Math.max(4, cellPx * 0.45)}
                  fill="#1976d2"
                  stroke="#fff"
                  strokeWidth={2}
                />
              </svg>
            )}
          </Box>
        )}

        <style>{`
          @keyframes receivingRackPulse {
            0%, 100% { transform: scale(1); filter: brightness(1); }
            50%      { transform: scale(1.08); filter: brightness(1.15); }
          }
          @keyframes receivingRouteDash {
            to { stroke-dashoffset: -20; }
          }
        `}</style>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          px: 1.25,
          py: 0.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Legend dotColor={COLOR_TARGET} label={t('screens.receiving.navMap.legendTarget')} />
        <Legend dotColor={COLOR_PLACED} label={t('screens.receiving.navMap.legendPlaced')} />
        <Legend dotColor={COLOR_PENDING} label={t('screens.receiving.navMap.legendPending')} />
        <Legend dotColor="#1976d2" label={t('screens.receiving.navMap.legendHere')} />
      </Box>
    </Paper>
  );
};

const Legend: React.FC<{ dotColor: string; label: string }> = ({ dotColor, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dotColor }} />
    <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1, color: 'text.secondary' }}>
      {label}
    </Typography>
  </Box>
);

export default ReceivingNavMap;
